/**
 * one-stop/theme — the tenant tint and the One-Stop stylesheet.
 *
 * THE VISUAL SOLL IS THE PROTOTYPE
 * Every rule below is LIFTED from `Lakeview OneStop.dc.html` — the same paddings,
 * radii, shadows, grid tracks and type steps, expressed as classes instead of
 * repeated inline styles. This is not a re-interpretation of the prototype: it is
 * the prototype's own geometry, with the DATA replaced by the document. Numbers
 * are the audit trail here — diff them against the prototype's inline styles.
 *
 * ONE DECLARATION SITE FOR THE TINT
 * The prototype declares the Lakeview maroon on `:root`. Here it is declared on
 * `.lv-root` instead — the portal subtree — for one reason that matters: the
 * SAME renderer mounts inside Studio for the Journey Builder preview, and a
 * `:root` override there would re-tint Studio's own chrome. Custom properties
 * cascade, so every `var(--jrni-*)` reference and every jrni-ui component inside
 * the subtree still picks the tint up. Re-tinting `--jrni-color-primary-1..4`
 * moves the header, the hero, the footer, the chips, the accents and every DS
 * component together, because the chrome shades are DERIVED from the tint with
 * `color-mix`, never pinned.
 *
 * WHERE A TENANT TINT PLUGS IN
 * `<OneStopTheme tint={...} />` takes a partial override of the brand steps.
 * Today nothing supplies one: the portal read family carries no theme tokens
 * (`getPortalPage` returns document chrome, blocks and session — no palette), so
 * the default below stands. When the tenant theme lands on the wire, the host
 * passes `tint={page.theme}` and NOTHING ELSE CHANGES. Until then the default is
 * the Lakeview maroon.
 */
import { useEffect } from "react";
import type { JSX } from "react";

/** The brand steps a tenant may re-tint. Everything else derives. */
export interface OneStopTint {
  readonly primary1?: string;
  readonly primary2?: string;
  readonly primary3?: string;
  readonly primary4?: string;
  readonly primaryText?: string;
}

/* ONE DEFAULT ACROSS EVERY CUSTOMER SURFACE (KP, 2026-09-17). The unbranded
 * default was the Lakeview maroon — a fixture tenant's colour — while the
 * booking journeys and the Studio One Stop tab default to the platform purple
 * (`DEFAULTS.brandColor` / `BRAND_FALLBACK` / `JOURNEY_BRAND_DEFAULT`, all
 * #5142C7). A visitor who tapped Book on a maroon board landed on a purple
 * journey. The ramp below is the same derivation `onestop-tab.tsx#tintFor`
 * applies to a served brand, so a re-tint and the default cannot disagree. */
const DEFAULT_BRAND = "#5142C7";
const DEFAULT_TINT: Required<OneStopTint> = {
  primary1: DEFAULT_BRAND,
  primary2: `color-mix(in srgb, ${DEFAULT_BRAND} 26%, #fff)`,
  primary3: `color-mix(in srgb, ${DEFAULT_BRAND} 13%, #fff)`,
  primary4: `color-mix(in srgb, ${DEFAULT_BRAND} 6%, #fff)`,
  primaryText: `color-mix(in srgb, ${DEFAULT_BRAND} 82%, #000)`,
};

const STYLE_ID = "lv-one-stop-styles";

function tintBlock(tint: OneStopTint): string {
  const v = { ...DEFAULT_TINT, ...tint };
  return `
.lv-root {
  --jrni-color-primary-1: ${v.primary1};
  --jrni-color-primary-2: ${v.primary2};
  --jrni-color-primary-3: ${v.primary3};
  --jrni-color-primary-4: ${v.primary4};
  --jrni-color-brand-fill: ${v.primary1};
  --jrni-color-primary-text: ${v.primaryText};

  /* Brand chrome shades — DERIVED from the tint, not pinned, so header, hero
   * and footer follow a re-tint instead of staying maroon. */
  --lv-chrome-header: color-mix(in srgb, var(--jrni-color-primary-1) 34%, #000);
  --lv-chrome-hero:   color-mix(in srgb, var(--jrni-color-primary-1) 68%, #000);
  --lv-chrome-footer: color-mix(in srgb, var(--jrni-color-primary-1) 23%, #000);
  --lv-chrome-rule:   color-mix(in srgb, var(--jrni-color-primary-1) 72%, #000);
}`;
}

/**
 * The accent ramp for directory nodes. ui-config: a closed palette of category
 * accents, exactly the prototype's 13, drawn from the design system's tertiary
 * ramp plus four tenant extensions where the DS ramp runs out. It is a
 * VOCABULARY, not data — no department name appears here; `accentFor()` maps a
 * node id onto the ramp deterministically, so the same department keeps the same
 * colour across renders without the renderer knowing what it is called.
 */
const DEPT_RAMP = [
  "var(--jrni-color-tertiary-dark-blue)",
  "var(--jrni-color-tertiary-red)",
  "var(--jrni-color-tertiary-purple)",
  "var(--jrni-color-tertiary-light-blue)",
  "var(--jrni-color-tertiary-turquoise)",
  "var(--jrni-color-tertiary-yellow)",
  "var(--jrni-color-tertiary-green)",
  "var(--jrni-color-tertiary-orange)",
  "var(--jrni-color-tertiary-pink)",
  "#8A3B6E",
  "#6E5E4A",
  "#C24A6E",
  "#4A6E7A",
] as const;

export function accentFor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return DEPT_RAMP[hash % DEPT_RAMP.length]!;
}

/* ── the stylesheet ───────────────────────────────────────────── */

/**
 * Desktop is the prototype's wide layout; the `max-width: 640px` block is its
 * 390 px layout. The prototype switches with a preview toggle and a phone frame;
 * a shipped page switches on the viewport, which is why the toggle and the frame
 * are the two things NOT lifted. Every other number is the prototype's.
 */
const SHEET = `
/* COLUMN, not a block, so the footer can be pinned (CC 0053 §1). With
 * 'display: block' the remainder of 'min-height' painted as canvas BELOW the
 * last child, which is why a window taller than the content showed the footer
 * band floating mid-page and a 1440×900 check never did.
 *
 * The slack is absorbed by '::after', NOT by a content block: growing the last
 * block would stretch whatever background it carries, and growing "the body"
 * is not available — blocks are flat siblings of the root here, so there is no
 * single content container to hand 'flex: 1' to. See the spacer below.
 *
 * '100dvh' second: iOS Safari's '100vh' overshoots by the toolbar height. Older
 * engines keep the '100vh' line. */
.lv-root {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--jrni-color-surface-canvas);
  font-family: var(--jrni-font-family-sans);
  color: var(--jrni-color-neutral-1);
}
.lv-root a { color: var(--jrni-color-primary-text); text-decoration: none; }
.lv-root a:hover { color: var(--jrni-color-primary-1); text-decoration: underline; }
.lv-root button { font-family: inherit; }

/* — header — */
.lv-header { background: var(--lv-chrome-header); }
.lv-header-inner {
  padding: 0 28px; display: flex; align-items: center; justify-content: space-between;
  height: 56px; gap: 24px;
}
.lv-brand { display: flex; align-items: center; gap: 11px; min-width: 0; }
.lv-brand-mark {
  width: 28px; height: 28px; border-radius: var(--jrni-radius-sm);
  background: var(--jrni-color-primary-1); color: var(--jrni-color-base-white);
  display: flex; align-items: center; justify-content: center;
  font-weight: var(--jrni-font-weight-bold); font-size: var(--jrni-font-size-L1);
  letter-spacing: 0.02em; flex: none;
}
.lv-brand-name {
  font-size: var(--jrni-font-size-H3); font-weight: var(--jrni-font-weight-bold);
  letter-spacing: -0.01em; color: var(--jrni-color-base-white);
}
.lv-brand-sub { font-size: var(--jrni-font-size-L1); color: rgba(255,255,255,0.62); }
.lv-nav { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
.lv-nav a {
  font-size: var(--jrni-font-size-P2); font-weight: var(--jrni-font-weight-medium);
  color: rgba(255,255,255,0.76); text-decoration: none;
}
.lv-nav a:hover { color: var(--jrni-color-base-white); text-decoration: none; }
.lv-nav a[aria-current="page"] {
  font-weight: var(--jrni-font-weight-semibold); color: var(--jrni-color-base-white);
}

/* — hero — */
.lv-hero {
  background: var(--lv-chrome-hero); color: var(--jrni-color-base-white);
  padding: 52px 28px 56px; display: flex; flex-direction: column; gap: 16px;
}
.lv-hero > .lv-banner { margin: 0; }
.lv-hero-grid {
  display: grid; grid-template-columns: minmax(0,1.35fr) minmax(0,1fr);
  gap: 28px; align-items: start;
}
.lv-hero-grid.lv-solo { grid-template-columns: minmax(0,1fr); }
.lv-hero-eyebrow {
  display: block;
  font-size: var(--jrni-font-size-L1); letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--jrni-color-primary-2); font-weight: var(--jrni-font-weight-bold);
}
.lv-searchcard {
  background: var(--jrni-color-base-white); border-radius: var(--jrni-radius-md);
  padding: 14px; box-shadow: var(--jrni-shadow-lg);
  display: flex; flex-direction: column; gap: 11px;
}
.lv-searchrow { display: flex; gap: 10px; align-items: stretch; margin: 0; }
/* The row stretches its children, but the shared Button carries an explicit
   height - 44px at the lg tier, against the Input's 48px box - so stretching
   cannot reach it and the button sat 4px proud of the field. Centring the slot
   puts the two on one optical line without either component forking its size
   scale. (No backticks in this file: the stylesheet is a template literal, and
   one would end it here rather than at the bottom of the file.) */
.lv-searchrow .lv-act { align-items: center; }
.lv-searchfield { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; }
/* The search row takes its shape from the width of the CARD, not the viewport
 * (2026-09-25). The CTA is a fixed 176px, so the field got whatever was left:
 * at 768 the hero is two columns, the card is about 393px, and the field shrank
 * to about 150px beside the button - narrower than the button that submits it.
 * At 390 the phone rule below already stacked the row but kept the 176px
 * button, half the width of the field above it. A container query asks the one
 * question that matters here - how wide is this card - so tablet, phone and a
 * narrow builder preview all get the same answer: below 440px of card content
 * the field and Find help each take the full width, the same full-width primary
 * the sign-in panel beside it already uses. At 1024 and up the card is about
 * 512px or more and the row is unchanged. The loading ghost renders this same
 * form and the same classes, so it follows without a rule of its own. */
.lv-searchcard { container: lv-search / inline-size; }
@container lv-search (max-width: 440px) {
  .lv-searchrow { flex-direction: column; }
  .lv-searchrow .lv-act, .lv-searchrow .lv-act > button { width: 100%; }
}
.lv-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.lv-chipwrap { display: inline-flex; }
/* Signed-in journey filter (All / Appointments / Walk-ins / Events). It sits between the
 * header and the hero band, so it needs its own vertical padding. Chip writes its padding
 * and font-size as inline styles, so '!important' is required here, as it is for the hero chips. */
/* Three classes, not two (2026-09-24): the filter also carries \`lv-first\`, and
   \`.lv-section.lv-first { padding-top: 44px }\` is declared LATER at the same
   weight, so it won and put 44px of white above the chips on a desk. */
.lv-section.lv-first.lv-journey-filter, .lv-section.lv-journey-filter { padding: 16px 28px; }
.lv-journey-filter > * { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.lv-journey-filter button {
  min-height: 34px !important; padding: 0 16px !important;
  font-size: 14px !important; font-weight: 500 !important;
  line-height: 1 !important; margin: 0 !important;
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
}

/* — the white panel: sign-in, my-visits — */
.lv-panel {
  background: var(--jrni-color-base-white); border-radius: var(--jrni-radius-md);
  padding: 18px; box-shadow: var(--jrni-shadow-lg);
  display: flex; flex-direction: column; gap: 14px;
}
.lv-panel-eyebrow {
  font-size: var(--jrni-font-size-L1); letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--jrni-color-primary-text); font-weight: var(--jrni-font-weight-bold);
}
.lv-panel-title {
  font-size: var(--jrni-font-size-H3); font-weight: var(--jrni-font-weight-bold);
  color: var(--jrni-color-chrome-ink); line-height: 1.25;
}
.lv-panel-rule {
  padding-top: 12px; border-top: 1px solid var(--jrni-color-surface-border);
  display: flex; flex-direction: column; gap: 6px;
}
.lv-soft { font-size: var(--jrni-font-size-L1); line-height: 1.45; color: var(--jrni-color-text-soft); }
.lv-linkbtn {
  align-self: flex-start; font-size: var(--jrni-font-size-L1);
  font-weight: var(--jrni-font-weight-bold); color: var(--jrni-color-primary-text);
  background: transparent; border: 0; padding: 0; cursor: pointer;
}
.lv-linkbtn:disabled { cursor: default; opacity: 0.55; }

/* — sections — */
.lv-section { max-width: none; margin: 0; padding: 48px 28px 0; }
.lv-section.lv-first { padding-top: 44px; }
.lv-section.lv-last { padding-bottom: 88px; }
/* The prototype's last section carries 88px of bottom padding before the footer
 * band; the footer is a BLOCK here, so the breathing room lives on the join. */
.lv-footer { margin-top: 88px; }
.lv-root > .lv-section:last-child { padding-bottom: 88px; }
/* The pin. '::after' is the LAST flex item, so it is ordered BEFORE the footer
 * explicitly; every other item keeps 'order: 0' and therefore document order.
 * Zero-height and 'flex-grow: 1', so it is invisible when the content is taller
 * than the viewport and takes the whole surplus when it is not. The footer's
 * 88px join above survives untouched — the spacer sits above that margin. */
.lv-root::after { content: ""; flex: 1 0 auto; order: 0; }
.lv-root > .lv-footer { order: 1; flex: none; }
.lv-section-head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 20px; margin-bottom: 14px;
}
.lv-section-headings { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.lv-h2 {
  margin: 0; font-size: var(--jrni-font-size-H2); font-weight: var(--jrni-font-weight-bold);
  letter-spacing: -0.01em;
}
.lv-sub { margin: 0; font-size: var(--jrni-font-size-P2); color: var(--jrni-color-text-soft); }
.lv-note { font-size: var(--jrni-font-size-L1); color: var(--jrni-color-text-soft); flex: none; }
.lv-overflow { margin: 10px 0 0; font-size: var(--jrni-font-size-L1); line-height: 1.45; color: var(--jrni-color-text-soft); }

/* — the two grids — */
.lv-grid { display: grid; grid-template-columns: repeat(auto-fill,minmax(300px,1fr)); gap: 10px; }
.lv-grid-tight { display: grid; grid-template-columns: repeat(auto-fill,minmax(244px,1fr)); gap: 8px; }

/* — the standing card — */
.lv-card {
  background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-surface-border);
  border-radius: var(--jrni-radius-default); padding: 14px;
  display: flex; flex-direction: column; gap: 11px; box-shadow: var(--jrni-shadow-chrome);
}
.lv-card-title {
  font-size: var(--jrni-font-size-H3); font-weight: var(--jrni-font-weight-bold);
  color: var(--jrni-color-chrome-ink); line-height: 1.25;
}
.lv-card-meta {
  display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: var(--jrni-font-size-L1);
  color: var(--jrni-color-neutral-2); line-height: 1.4;
}
.lv-dot {
  width: 6px; height: 6px; border-radius: var(--jrni-radius-pill);
  background: var(--jrni-color-success-fill); display: inline-block; flex: none;
}
.lv-bignum { font-size: var(--jrni-font-size-H1ExtraLarge); font-weight: var(--jrni-font-weight-bold); line-height: 1; }

/* — the clickable row, which becomes a list item on mobile — */
.lv-rowlist { display: grid; grid-template-columns: repeat(auto-fill,minmax(300px,1fr)); gap: 10px; }
.lv-rowlist.lv-tight { gap: 8px; }
.lv-row {
  width: 100%; text-align: left; color: inherit;
  background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-surface-border);
  border-radius: var(--jrni-radius-default); padding: 14px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; cursor: pointer;
}
.lv-row.lv-compact { border-radius: var(--jrni-radius-sm); padding: 10px 12px; align-items: flex-start; gap: 10px; cursor: default; }
.lv-row:hover { border-color: var(--jrni-color-primary-1); background: var(--jrni-color-surface-card-soft); }
.lv-row.lv-compact:hover { border-color: var(--jrni-color-surface-border); background: var(--jrni-color-base-white); }
.lv-row:disabled { cursor: default; }
.lv-row:disabled:hover { border-color: var(--jrni-color-surface-border); background: var(--jrni-color-base-white); }
.lv-row-main { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.lv-row-title {
  font-size: var(--jrni-font-size-P1); font-weight: var(--jrni-font-weight-bold);
  color: var(--jrni-color-chrome-ink); line-height: 1.3;
}
.lv-row-detail { font-size: var(--jrni-font-size-L1); color: var(--jrni-color-text-soft); line-height: 1.4; }
.lv-row-side { flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }

/* — a row that can open a form beneath its header —
 *
 * The header row keeps the geometry it always had: date tile, title column,
 * action column, same gap, same alignment. What changes is that the row itself
 * is now a COLUMN, so a form is a sibling of that header rather than a child of
 * the title column — which is what the events form was, at roughly 150px wide,
 * halved again by the two-column name row, giving 75px inputs and labels that
 * wrapped mid-word. A form belongs to the card, not to one of its columns. */
.lv-row.lv-row-stack { flex-direction: column; align-items: stretch; }
.lv-row-head {
  width: 100%; display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
}
.lv-row-form { width: 100%; display: flex; flex-direction: column; gap: 10px; }

/* — a row that carries a CTA: the title gets the whole card (2026-09-25) —
 *
 * The side column sat BESIDE the title at the CTA's fixed 176px width. In the
 * 339px card a desk draws, and the 350px card a tablet draws, that left the
 * title and its detail line about 120px: a three-word service name wrapped to
 * three lines, a staff list to six, and an event title to one word a line.
 *
 * The side column now wraps UNDER the title as a line of its own - the soonest
 * time on the left when there is one, the CTA pushed right by the cluster's own
 * auto margin. That is the shape the queue card already has (title on top,
 * action bottom right), so the three card rows now read as one family. It is
 * one layout at every width rather than a breakpoint, so a card never changes
 * shape between a desk and a tablet. On an event row the class sits on the
 * HEAD, so the date tile and the title stay side by side above the CTA line.
 * Rows without a CTA - the whole-row button, the hold and slot-gone states -
 * do not carry the class and keep the geometry they had. The side line wraps
 * too, so on a 320px device the time and the CTA stack instead of overflowing. */
.lv-row.lv-row-cta, .lv-row-head.lv-row-cta { flex-wrap: wrap; align-items: flex-start; row-gap: 10px; }
.lv-row-cta > .lv-row-main { flex: 1 1 0; }
.lv-row-cta > .lv-row-side {
  flex: 1 0 100%; flex-direction: row; flex-wrap: wrap;
  align-items: center; justify-content: space-between; gap: 8px 12px;
}

.lv-pill-action {
  flex: none; font-size: var(--jrni-font-size-L1); font-weight: var(--jrni-font-weight-bold);
  color: var(--jrni-color-primary-text); background: var(--jrni-color-primary-4);
  border: 1px solid var(--jrni-color-primary-2); border-radius: var(--jrni-radius-pill);
  padding: 7px 12px; white-space: nowrap; text-decoration: none;
}

/* — the date tile on an event row — */
.lv-datetile { width: 32px; flex: none; display: flex; flex-direction: column; align-items: center; }
.lv-datetile-mon {
  font-size: var(--jrni-font-size-L1); letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--jrni-color-primary-text); font-weight: var(--jrni-font-weight-bold);
}
.lv-datetile-day {
  font-size: var(--jrni-font-size-H2); font-weight: var(--jrni-font-weight-bold);
  line-height: 1.05; color: var(--jrni-color-chrome-ink);
}

/* — the disclosure card: directory + faq — */
.lv-disclosure {
  background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-surface-border);
  border-radius: var(--jrni-radius-default); overflow: hidden;
}
.lv-disclosure-head {
  width: 100%; text-align: left; color: inherit; background: transparent; border: 0;
  padding: 16px 18px; display: flex; align-items: center; justify-content: space-between;
  gap: 16px; cursor: pointer;
}
.lv-disclosure-head:hover { background: var(--jrni-color-primary-4); }
.lv-disclosure-body { padding: 0 18px 18px; }
.lv-disclosure-more { flex: none; font-size: var(--jrni-font-size-L1); font-weight: var(--jrni-font-weight-bold); color: var(--jrni-color-primary-text); }
.lv-accent-card {
  text-align: left; background: var(--jrni-color-base-white);
  border: 1px solid var(--jrni-color-surface-border); border-radius: var(--jrni-radius-sm);
  padding: 10px 12px; display: flex; flex-direction: column; gap: 5px;
}
.lv-accent-name { font-size: var(--jrni-font-size-P2); font-weight: var(--jrni-font-weight-bold); color: var(--jrni-color-chrome-ink); line-height: 1.3; }
.lv-accent-meta {
  font-size: var(--jrni-font-size-L1); letter-spacing: 0.05em; text-transform: uppercase;
  font-weight: var(--jrni-font-weight-bold);
}

/* — the brand-tinted aside: quote — */
.lv-tinted {
  background: var(--jrni-color-primary-4); border: 1px solid var(--jrni-color-primary-2);
  border-radius: var(--jrni-radius-default); padding: 14px;
  display: flex; flex-direction: column; gap: 10px;
}
.lv-tinted-body { font-size: var(--jrni-font-size-L1); line-height: 1.45; color: var(--lv-chrome-rule); }

/* — the flat banner — */
.lv-banner {
  padding: 12px 16px; border-radius: var(--jrni-radius-default); margin: 0 0 8px;
  font-size: var(--jrni-font-size-P2); line-height: 1.45;
  background: var(--jrni-color-surface-card-soft); border: 1px solid var(--jrni-color-surface-border);
  color: var(--jrni-color-neutral-1);
}
.lv-banner.lv-highlight { background: var(--jrni-color-primary-4); border-color: var(--jrni-color-primary-2); }
/* The notice's optional furniture: the tone glyph on the left, the dismiss on
 * the right. Both are registry settings ('showIcon', 'dismissible'), so the row
 * only becomes a row when one of them is on — a notice with neither renders the
 * flat band it always did, unchanged. */
.lv-banner-row { display: flex; align-items: flex-start; gap: 10px; }
.lv-banner-icon { flex: none; display: flex; padding-top: 2px; }
.lv-banner-body { min-width: 0; flex: 1; }
.lv-banner-dismiss {
  flex: none; display: flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; margin: -2px -4px 0 0;
  color: inherit; background: transparent; border: 0; border-radius: var(--jrni-radius-sm);
  cursor: pointer; opacity: 0.65;
}
.lv-banner-dismiss:hover { opacity: 1; }

/* — the empty state — */
.lv-empty {
  padding: 18px 16px; border: 1px dashed var(--jrni-color-neutral-4);
  border-radius: var(--jrni-radius-default); color: var(--jrni-color-text-soft);
  font-size: var(--jrni-font-size-P2); margin: 0;
}

/* — success surface, lifted from the prototype's join/confirm panels — */
.lv-success {
  background: var(--jrni-color-semantic-green-2); border-radius: var(--jrni-radius-default);
  padding: 14px; display: flex; align-items: center; justify-content: space-between; gap: 18px;
}
.lv-success-eyebrow {
  font-size: var(--jrni-font-size-L1); letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--jrni-color-status-success-text); font-weight: var(--jrni-font-weight-semibold);
}
.lv-success-head {
  font-size: var(--jrni-font-size-H2); font-weight: var(--jrni-font-weight-bold);
  letter-spacing: -0.01em; color: var(--jrni-color-status-success-text);
}
.lv-success-meta { font-size: var(--jrni-font-size-L1); color: var(--jrni-color-status-success-text); }

/* — a reference a visitor reads out, and the action beside it —
 *
 * 'user-select: all' rather than plain selectable text: a ticket id is quoted at
 * a desk and typed into another device, so one tap has to take the WHOLE code
 * and not the word beside it or one hyphen-delimited half. 'nowrap' for the same
 * reason — a code broken across two lines is copied broken.
 *
 * The copy cluster is INLINE-flex, so it flows after the sentence it belongs to
 * instead of turning that sentence's own words into flex items. */
.lv-code { user-select: all; -webkit-user-select: all; white-space: nowrap; }
.lv-copyrow { display: inline-flex; align-items: center; gap: 6px; vertical-align: middle; margin-left: 8px; }
.lv-copied {
  font-size: var(--jrni-font-size-L1); font-weight: var(--jrni-font-weight-semibold);
  letter-spacing: 0; text-transform: none; color: var(--jrni-color-status-success-text);
}

/* — CTA cluster: auto-width on desktop, split full-width on mobile — */
/* KP 2026-09-21: every CTA the same size, right-aligned in its block, label centred. */
.lv-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; justify-content: flex-end; margin-left: auto; }
.lv-act { flex: 0 0 auto; display: inline-flex; }
.lv-act > button { width: 176px; justify-content: center; text-align: center; }
.lv-slotlist > .lv-act > button { width: 100%; }
.lv-cta-reason { margin: 0; font-size: var(--jrni-font-size-L1); color: var(--jrni-color-text-soft); line-height: 1.4; }
.lv-cta-error { margin: 0; font-size: var(--jrni-font-size-L1); color: var(--jrni-color-status-danger-text); line-height: 1.4; }

/* — the details a CTA has to collect before it can call anything — */
.lv-visitor { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
.lv-visitor-wide { grid-column: 1 / -1; }
.lv-visitor-legend {
  grid-column: 1 / -1; font-size: var(--jrni-font-size-L1);
  letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--jrni-color-primary-text); font-weight: var(--jrni-font-weight-bold);
}
/* The one nudge before the first submit. After that the fields carry their own
 * messages and this line stands down, so it never competes with them. */
.lv-visitor-hint {
  grid-column: 1 / -1; font-size: var(--jrni-font-size-L1);
  color: var(--jrni-color-text-soft); line-height: 1.4;
}
.lv-slotlist { display: flex; flex-wrap: wrap; gap: 6px; }

/* — stats — */
.lv-stats { display: grid; grid-template-columns: repeat(auto-fit,minmax(160px,1fr)); gap: 10px; }
.lv-stat {
  background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-surface-border);
  border-radius: var(--jrni-radius-default); padding: 14px;
  display: flex; flex-direction: column; gap: 4px; box-shadow: var(--jrni-shadow-chrome);
}
.lv-stat-value { font-size: var(--jrni-font-size-H1ExtraLarge); font-weight: var(--jrni-font-weight-bold); line-height: 1; color: var(--jrni-color-primary-text); }
.lv-stat-label { font-size: var(--jrni-font-size-L1); color: var(--jrni-color-text-soft); line-height: 1.4; }

/* — footer — */
.lv-footer { background: var(--lv-chrome-footer); color: rgba(255,255,255,0.7); }
.lv-footer-grid {
  max-width: none; margin: 0; padding: 22px 28px;
  display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 24px;
  font-size: var(--jrni-font-size-L1); line-height: 1.6;
}
.lv-footer-col { display: flex; flex-direction: column; gap: 6px; }
.lv-footer-col strong { color: var(--jrni-color-base-white); font-weight: var(--jrni-font-weight-semibold); }
.lv-footer a { color: rgba(255,255,255,0.7); }
.lv-footer a:hover { color: var(--jrni-color-base-white); }

/* — the builder host's unsupported-block card — */
.lv-unsupported {
  margin: 24px 28px 0; padding: 14px 16px;
  border: 1px dashed var(--jrni-color-status-warning-text); border-radius: var(--jrni-radius-default);
  background: var(--jrni-color-semantic-orange-2);
  display: flex; flex-direction: column; gap: 4px;
}
.lv-unsupported-title { font-size: var(--jrni-font-size-P2); font-weight: var(--jrni-font-weight-bold); color: var(--jrni-color-status-warning-text); }
.lv-unsupported-body { font-size: var(--jrni-font-size-L1); color: var(--jrni-color-neutral-2); line-height: 1.45; }
.lv-unsupported-meta { font-family: var(--jrni-font-family-mono, ui-monospace, monospace); font-size: var(--jrni-font-size-L1); color: var(--jrni-color-text-soft); }

/* ── the prototype's 390 px layout ─────────────────────────────────────── */
@media (max-width: 640px) {
  .lv-header-inner { padding: 11px 16px; height: auto; gap: 12px; flex-wrap: wrap; }
  .lv-brand-name { font-size: var(--jrni-font-size-P1); }
  .lv-nav { gap: 14px; }
  .lv-nav a { font-size: var(--jrni-font-size-L1); }

  .lv-hero { padding: 18px 16px 20px; gap: 13px; }
  .lv-hero-grid { grid-template-columns: minmax(0,1fr); gap: 13px; }
  .lv-searchcard { background: transparent; box-shadow: none; padding: 0; gap: 9px; }
  /* The card goes transparent here, so its own heading and subtitle are suddenly
   * sitting on the maroon hero. Chrome-ink on maroon measures about 1.3:1 — the
   * words survive as a shape, not as text. Re-coloured for the surface they are
   * actually on, the same reason the hero eyebrow already uses primary-2. */
  .lv-searchcard > .lv-panel-title { color: var(--jrni-color-base-white); }
  .lv-searchcard > .lv-soft { color: var(--jrni-color-primary-2); }
  .lv-searchcard .lv-visitor-legend { color: var(--jrni-color-primary-2); }
  .lv-searchrow { flex-direction: column; gap: 9px; }
  .lv-chips { gap: 8px; }
  /* The prototype's mobile chips are on-brand pills against the hero, not the
   * DS Chip's white pill — same component, hero treatment.
   *
   * '!important' is the MECHANISM, chosen deliberately (CC's finding 1). Chip
   * sets background / colour / border / font-size / padding as INLINE styles on
   * this same button, and an inline declaration outranks any normal stylesheet
   * rule however specific — so this block was inert and the desktop white pill
   * rendered on the maroon hero. The alternatives were worse: Chip has no
   * on-brand variant to ask for (only 'selected', which is the brand-TINTED
   * state, and 'effect: "inert"', which is hollow + dashed), and its 'style'
   * prop is spread last but is a single object with no viewport axis — passing
   * the hero treatment there would put it on the desktop chips too. A wrapper
   * cannot reach inside to the element carrying the inline styles at all.
   * If Chip ever gains an on-brand variant, this block deletes and the prop
   * replaces it. */
  .lv-chipwrap > button {
    color: var(--jrni-color-base-white) !important;
    background: var(--jrni-color-on-brand-hover) !important;
    border-color: var(--jrni-color-on-brand-edge) !important;
    font-size: var(--jrni-font-size-P2) !important;
    padding: 9px 14px !important;
  }
  .lv-panel { padding: 14px; gap: 10px; box-shadow: none; }

  .lv-section { padding: 22px 16px 0; }
  .lv-section.lv-first { padding-top: 22px; }
  .lv-section.lv-first.lv-journey-filter, .lv-section.lv-journey-filter { padding: 12px 16px; }
  .lv-journey-filter > * { gap: 6px; }
  .lv-journey-filter button { min-height: 40px !important; padding: 0 14px !important; font-size: 14px !important; }
  .lv-section.lv-last { padding-bottom: 32px; }
  .lv-root > .lv-section:last-child { padding-bottom: 32px; }
  .lv-footer { margin-top: 32px; }
  .lv-section-head { align-items: baseline; gap: 10px; margin-bottom: 10px; }

  .lv-grid, .lv-grid-tight { grid-template-columns: minmax(0,1fr); gap: 10px; }
  .lv-card { border-radius: var(--jrni-radius-md); }

  /* Separate cards become one bordered list, exactly as the prototype does. */
  .lv-rowlist {
    display: block;
    background: var(--jrni-color-base-white);
    border: 1px solid var(--jrni-color-surface-border);
    border-radius: var(--jrni-radius-md);
    overflow: hidden;
  }
  .lv-rowlist > .lv-row {
    border: 0; border-bottom: 1px solid var(--jrni-color-surface-border);
    border-radius: 0; padding: 13px 14px;
  }
  .lv-rowlist > .lv-row:last-child { border-bottom: 0; }
  .lv-rowlist > .lv-row:hover { border-color: transparent; border-bottom-color: var(--jrni-color-surface-border); }
  .lv-row-title { font-weight: var(--jrni-font-weight-semibold); }
  .lv-datetile { width: 38px; }
  .lv-datetile-day { line-height: 1.1; }

  .lv-disclosure { border-radius: var(--jrni-radius-md); }
  .lv-disclosure-head { padding: 14px; }
  .lv-disclosure-body { padding: 0 14px 14px; }
  .lv-disclosure-body .lv-grid-tight { grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
  .lv-accent-card { border-radius: var(--jrni-radius-default); padding: 10px; gap: 3px; }
  .lv-accent-name { font-size: var(--jrni-font-size-L1); line-height: 1.25; }

  /* — Desk actions go full width and split the row.
   *
   * WRAP, with a 140px floor per control. The row still splits into equal halves
   * wherever there is room for two — at 390 the cluster measures 330px inside the
   * card, so two controls land at 161px each, exactly as before — and below about
   * 288px of cluster the buttons take their own lines instead of shrinking into
   * two unreadable stubs. 'flex: 1 1 0' is kept so the halves stay equal rather
   * than content-sized; only the floor and the wrap are new. */
  /* KP 2026-09-21: no full-width split on mobile either — one fixed size, right-aligned. */
  .lv-actions { gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
  .lv-act { flex: 0 0 auto; }
  .lv-act > button { width: 176px; }
  /* Slot choices are a COLUMN here, not a wrapped row — and the fill rule
   * directly above is why. The alternatives list reuses '.lv-act', so at 390
   * every slot took 'flex: 1 1 0' into one 328px line: eight buttons at 22px
   * with 15px of each sitting UNDER its neighbour, and 'elementFromPoint' at
   * the first button's centre returning the SECOND one. A thumb aiming at 8:00
   * held 8:10, and that hold is a real record at a time nobody chose (CC 0020
   * finding 1). It is also the path a visitor reaches precisely BECAUSE their
   * first choice was taken — the one they are most likely to be hurrying.
   *
   * Grid, because a grid item ignores flex-grow / flex-shrink / flex-basis
   * outright: one full-width slot per row, and the collapse cannot come back
   * through a rule written for the desk-action cluster. 'min-height' is the
   * 44px touch minimum, and it beats Button's INLINE 'height: 32px' because
   * min-height always wins the box computation however the height was set —
   * which is also why the 'size' prop is not the mechanism here: it has no
   * viewport axis, and its next step up is 48. */
  .lv-slotlist { display: grid; grid-template-columns: minmax(0,1fr); gap: 8px; }
  .lv-slotlist > .lv-act > button { min-height: 44px; }

  .lv-stats { grid-template-columns: repeat(2,minmax(0,1fr)); }

  .lv-footer { background: var(--jrni-color-chrome-ink); }
  .lv-footer-grid { grid-template-columns: minmax(0,1fr); gap: 14px; padding: 18px 16px; }

  .lv-unsupported { margin: 16px 16px 0; }
}

/* ── 'Your details' from tablet portrait down ──────────────────── */

/**
 * 768, NOT 640, AND ONLY THIS FORM.
 *
 * The form's own rules were inside the 640 block, which is the prototype's phone
 * layout — so between 641 and 768 the DESKTOP two-column form was still in force,
 * and on the queue join email and the mobile number shared a row inside a card
 * that had stopped being wide. A half-width email field shows about a dozen
 * characters of an address the visitor is trying to check; tablet portrait is 768
 * and that is where it was worst.
 *
 * Raising the 640 block itself was the wrong lever: it also carries the header,
 * hero, section, grid and row-list layouts, and moving those onto tablets is a
 * redesign nobody asked for. So this is a SECOND query holding only the four
 * rules the form owns, and the phone block above keeps everything else exactly
 * where it was. At 640 and below the two agree, so nothing is fighting.
 *
 * THE NAME PAIR IS A FLOOR, NOT A BREAKPOINT. First and last name stay side by
 * side only while each track can still be 140px wide, and the grid drops them to
 * one per row by itself below that — 'auto-fit' + 'minmax' states exactly that
 * rule, which is why widening the query does not need any of these numbers
 * re-picked. At 768 the two tracks are roughly 340px, at 390 about 161px, and on
 * a 320px device the same rule stacks them.
 *
 * EMAIL AND THE MOBILE NUMBER ARE ONE PER ROW WHATEVER THE WIDTH. They are the
 * two longest values on the form and the two most often mistyped. Their helper
 * lines are Input's own and sit under each field unchanged.
 *
 * 44px IS SET ON THE BOX, NOT ON THE INPUT. Input draws its height as an inline
 * 'height' on the bordered row that wraps the field, so a rule on 'input' would
 * raise the touch target while leaving the visible control at 40px. ':has(>
 * input)' names that row without reaching into the component, and 'min-height'
 * beats an inline 'height' because the used height is the larger of the two
 * however the height was set — the same mechanism as the slot buttons above.
 *
 * 16px, because anything smaller makes mobile Safari ZOOM THE VIEWPORT when a
 * field takes focus — the page jumps, the card leaves the screen, and the student
 * is typing into a layout that moved under them. The design system has this exact
 * step (its 'lg' control size uses it for precisely this reason); what it does
 * not have is a viewport axis, and 'size' is a prop, not a media query. Input
 * writes its font-size as an INLINE style on the element, and an inline
 * declaration outranks any normal rule however specific, so '!important' is the
 * mechanism — the same one, for the same reason, as the hero chips above. If Input
 * ever takes a responsive size, that rule deletes.
 */
@media (max-width: 768px) {
  .lv-visitor { grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); }
  .lv-visitor-email, .lv-visitor-phone { grid-column: 1 / -1; }
  .lv-visitor div:has(> input) { min-height: 44px; }
  .lv-visitor input { font-size: 16px !important; width: 100%; }
}
`;

/**
 * Mounts the stylesheet and the tint. Idempotent: one `<style>` per document,
 * rewritten when the tint changes, so mounting the renderer twice (portal page +
 * a builder preview in the same document) does not duplicate rules.
 *
 * Also sets `data-density="comfortable"` on `<body>`: the design system's layout
 * reset targets `body:not([data-density]) main > section` with `!important`
 * padding, which would otherwise flatten every section's authored spacing. The
 * prototype does exactly this, for exactly this reason. The previous value is
 * restored on unmount, so mounting a preview inside Studio cannot leave Studio's
 * own density flag changed.
 */
export function OneStopTheme({ tint = {} }: { tint?: OneStopTint }): JSX.Element | null {
  const css = `${tintBlock(tint)}\n${SHEET}`;
  useEffect(() => {
    const doc = typeof document === "undefined" ? null : document;
    if (!doc) return;
    let node = doc.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!node) {
      node = doc.createElement("style");
      node.id = STYLE_ID;
      doc.head.appendChild(node);
    }
    if (node.textContent !== css) node.textContent = css;
    const previousDensity = doc.body.getAttribute("data-density");
    doc.body.setAttribute("data-density", "comfortable");
    return () => {
      if (previousDensity === null) doc.body.removeAttribute("data-density");
      else doc.body.setAttribute("data-density", previousDensity);
    };
  }, [css]);
  return null;
}
